import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const TestProfiles = () => {
  const { user } = useAuth();
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [currentProfile, setCurrentProfile] = useState<any>(null);

  useEffect(() => {
    checkCurrentProfile();
  }, []);

  const checkCurrentProfile = async () => {
    try {
      // Obtener el perfil y el rol
      const [profileResult, roleResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user?.id)
          .single(),
        supabase
          .from('user_roles')
          .select('*')
          .eq('user_id', user?.id)
          .single()
      ]);

      if (profileResult.error && roleResult.error) {
        console.error('Error checking profile:', profileResult.error);
        console.error('Error checking role:', roleResult.error);
        setCurrentProfile(null);
      } else {
        const combinedData = {
          profile: profileResult.data,
          role: roleResult.data
        };
        console.log('Current profile and role:', combinedData);
        setCurrentProfile(combinedData);
      }
    } catch (error) {
      console.error('Error in checkCurrentProfile:', error);
      setCurrentProfile(null);
    }
  };

  const insertTestProfile = async () => {
    try {
      setLoading(true);
      setResult('');

      console.log('Attempting to initialize profile via RPC...');
      const { data: rpcResult, error: rpcError } = await supabase
        .rpc('initialize_user_profile', {
          user_id: user?.id,
          user_email: user?.email
        });

      if (rpcError) {
        console.error('RPC error:', rpcError);
        setResult(`Profile initialization failed: ${rpcError.message}`);
      } else {
        console.log('RPC success:', rpcResult);
        setResult(`Profile initialized successfully: ${JSON.stringify(rpcResult, null, 2)}`);
        await checkCurrentProfile();
      }
    } catch (error) {
      console.error('Error in test:', error);
      setResult(`Unexpected error: ${JSON.stringify(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const readAllProfiles = async () => {
    try {
      setLoading(true);
      setResult('');

      console.log('Attempting to read all profiles...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*');

      if (error) {
        console.error('Read error:', error);
        setResult(`Read failed: ${error.message}`);
      } else {
        console.log('Read success:', data);
        setResult(`Found ${data.length} profiles:\n${JSON.stringify(data, null, 2)}`);
      }
    } catch (error) {
      console.error('Error reading profiles:', error);
      setResult(`Unexpected error: ${JSON.stringify(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-bold">Test Profiles Page</h1>
        
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-4 mb-6">
          <h2 className="text-lg font-medium mb-2">Current Profile Status</h2>
          {currentProfile ? (
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <p className="text-sm text-green-700">
                Profile exists with supervisor role: <strong>{currentProfile.role?.is_supervisor ? 'Yes' : 'No'}</strong>
              </p>
              <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto">
                {JSON.stringify(currentProfile, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <p className="text-sm text-yellow-700">No profile found for current user</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex space-x-4">
            <button
              onClick={insertTestProfile}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {loading ? 'Processing...' : 'Initialize Profile'}
            </button>

            <button
              onClick={readAllProfiles}
              disabled={loading}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-green-300"
            >
              {loading ? 'Reading...' : 'Read All Profiles'}
            </button>
          </div>

          {result && (
            <div className="mt-4">
              <h2 className="text-lg font-semibold mb-2">Result:</h2>
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
                {result}
              </pre>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TestProfiles;
